import React, { useState, useEffect } from 'react';
import { DefaultTheme, ThemeProvider } from 'styled-components';
import { theme } from './theme';
import { theme as themeEnogen } from './themeEnogen';
import { Routing } from './pages/Routing/Routing';
import { GlobalStyle } from './styles';
import { ThemeName } from './logic/store/UI/UISlice';
import * as queryString from 'query-string';
import { setMasquerade } from './logic/Api/Api';
import { useAppDispatch } from './logic/store/Store';
import { clearLoadingState } from './logic/store/User/UserInfoSlice';
import { uiSlice } from './logic/store/UI/UISlice';

const App = () =>
{
	const [currentThemeFile, setCurrentThemeFile] = useState<DefaultTheme>();
	const dispatch = useAppDispatch();

	// Handle bfcache restoration - reset loading states when page is restored from cache
	useEffect(() =>
	{
		const handlePageShow = (event: PageTransitionEvent) =>
		{
			// event.persisted is true when page is restored from bfcache
			if (event.persisted)
			{
				dispatch(clearLoadingState());
			}
		};

		window.addEventListener('pageshow', handlePageShow);
		
		return () =>
		{
			window.removeEventListener('pageshow', handlePageShow);
		};
	}, [dispatch]);

	// Parse URL parameters once per location.search change and dispatch
	// the corresponding actions. Doing this in an effect (instead of during
	// render) prevents repeated dispatches on every re-render, which could
	// otherwise clobber user-driven state changes and re-trigger post-login
	// redirect flows.
	useEffect(() =>
	{
		const parsed = queryString.parse(location.search);

		if (parsed.masquerade)
		{
			setMasquerade(String(parsed.masquerade));
		}

		if (typeof parsed.sellerFoundationId === 'string' && parsed.sellerFoundationId.length > 0)
		{
			dispatch(uiSlice.actions.setInitialUserFoundationId(parsed.sellerFoundationId));
		}
		if (typeof parsed.growerFoundationId === 'string' && parsed.growerFoundationId.length > 0)
		{
			dispatch(uiSlice.actions.setInitialGrowerFoundationId(parsed.growerFoundationId));
		}
		if (typeof parsed.orderId === 'string' && parsed.orderId.length > 0)
		{
			dispatch(uiSlice.actions.setInitialSalesforceOrderId(parsed.orderId));
		}

		const tabParam = typeof parsed.tab === 'string' ? parsed.tab : undefined;
		const yearParam = typeof parsed.year === 'string' && parsed.year.length > 0 ? parsed.year : undefined;
		const validTabs = ['Plan', 'Execute', 'Enogen'] as const;
		const matchedTab = tabParam
			? validTabs.find(t => t.toLowerCase() === tabParam.toLowerCase())
			: undefined;
		if (matchedTab)
		{
			dispatch(uiSlice.actions.setSelectedDashboardTab(matchedTab));
		}
		else if (yearParam)
		{
			// Year provided without an explicit tab → default to Plan.
			dispatch(uiSlice.actions.setSelectedDashboardTab('Plan'));
		}
		if (yearParam)
		{
			dispatch(uiSlice.actions.setSelectedYear(yearParam));
		}
	}, [location.search, dispatch]);
	
	const changeTheme = (name: ThemeName) =>
	{
		switch (name) 
		{
			case ThemeName.Enogen:
			{
				setCurrentThemeFile(themeEnogen);

				// Change the favicon
				const favicon = document.getElementById('favicon');
				favicon.setAttribute('href', '/enogen_favicon.ico');

				// Change the site title
				document.title = 'E-Luminate';

				break;
			}
			default:
			{
				setCurrentThemeFile(theme);

				// Change the favicon
				const favicon = document.getElementById('favicon');
				favicon.setAttribute('href', '/favicon.ico');

				// Change the site title
				document.title = 'GHX Fields';
				
				break;
			}
		}
	};

	return (
		<ThemeProvider theme={currentThemeFile ?? theme}>
			<GlobalStyle />
			<Routing changeTheme={changeTheme} />
		</ThemeProvider>
	);
};

export default App;
